import {
    TapeIngestStatus,
    type TapeColumnMapping,
    type TapeRole,
    type TapeUpload,
} from '@lightdash/common';
import {
    Autocomplete,
    Badge,
    Button,
    FileInput,
    Group,
    Progress,
    ScrollArea,
    SegmentedControl,
    Select,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useState, type FC } from 'react';
import MantineModal from '../../components/common/MantineModal';
import { useCommitTape, useTapes, useUploadTape } from './useTapes';

type Props = { projectUuid: string; onClose: () => void };

const STATUS_COLOR: Record<string, string> = {
    [TapeIngestStatus.COMPLETE]: 'green',
    [TapeIngestStatus.BLOCKED]: 'red',
    [TapeIngestStatus.ERROR]: 'red',
    [TapeIngestStatus.INVALID_MAPPING]: 'red',
};

// How to read a column: date formats for date fields, units for rate-like fields.
const DATE_FORMATS = [
    { value: '', label: 'ISO date' },
    { value: 'quarter', label: 'Quarter (2025Q1)' },
    { value: '%d%b%Y', label: '01JAN2024' },
    { value: '%m/%d/%Y', label: 'MM/DD/YYYY' },
];
const UNITS = [
    { value: '', label: 'Decimal' },
    { value: 'percent', label: 'Percent (÷100)' },
];

const MappingReview: FC<{
    tape: TapeUpload;
    onCommit: (mapping: Record<string, TapeColumnMapping>) => void;
    committing: boolean;
}> = ({ tape, onCommit, committing }) => {
    const [mapping, setMapping] = useState<Record<string, string | null>>(() =>
        Object.fromEntries(
            tape.proposals.map((p) => [p.field, p.proposedColumn]),
        ),
    );
    const [readAs, setReadAs] = useState<Record<string, string>>({});
    const missing = tape.proposals.filter(
        (p) => p.required && !mapping[p.field],
    );
    const mappedCount = Object.values(mapping).filter(Boolean).length;
    // required fields first; the rest are optional context
    const rows = [...tape.proposals].sort(
        (a, b) => Number(b.required) - Number(a.required),
    );
    return (
        <Stack gap="sm">
            <Text size="sm" c="dimmed" ff="monospace">
                {tape.filename} · {(tape.sizeBytes / 1e9).toFixed(2)} GB ·{' '}
                {tape.headers.length.toLocaleString()} columns
            </Text>
            <Text size="sm">
                Confirm the source column for each field. Samples come from the
                head of the file; unmapped fields are left empty.
            </Text>
            <ScrollArea.Autosize mah="52vh">
                <Table data-testid="tape-mapping-table" stickyHeader>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Field</Table.Th>
                            <Table.Th>Source column</Table.Th>
                            <Table.Th>Read as</Table.Th>
                            <Table.Th>Sample values</Table.Th>
                            <Table.Th>Match</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((p) => (
                            <Table.Tr key={p.field}>
                                <Table.Td>
                                    <Text size="sm" fw={500}>
                                        {p.field}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {p.required ? 'required' : 'optional'} ·{' '}
                                        {p.type}
                                        {p.unit ? ` · ${p.unit}` : ''}
                                        {p.semantics ? ` · ${p.semantics}` : ''}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Select
                                        size="xs"
                                        searchable
                                        clearable={!p.required}
                                        aria-label={`source for ${p.field}`}
                                        data={tape.headers}
                                        value={mapping[p.field]}
                                        onChange={(v) =>
                                            setMapping((m) => ({
                                                ...m,
                                                [p.field]: v,
                                            }))
                                        }
                                    />
                                </Table.Td>
                                <Table.Td>
                                    {(p.type === 'date' ||
                                        p.unit === 'decimal') && (
                                        <Select
                                            size="xs"
                                            w={150}
                                            aria-label={`read ${p.field} as`}
                                            data={
                                                p.type === 'date'
                                                    ? DATE_FORMATS
                                                    : UNITS
                                            }
                                            value={readAs[p.field] ?? ''}
                                            onChange={(v) =>
                                                setReadAs((r) => ({
                                                    ...r,
                                                    [p.field]: v ?? '',
                                                }))
                                            }
                                        />
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    <Text size="xs" ff="monospace">
                                        {mapping[p.field] === p.proposedColumn
                                            ? (p.samples ?? []).join(' · ') ||
                                              '—'
                                            : '—'}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text
                                        size="xs"
                                        c={p.proposedColumn ? 'dimmed' : 'blue'}
                                    >
                                        {p.evidence}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea.Autosize>
            <Group justify="space-between">
                <Text size="sm" c="dimmed">
                    {mappedCount} of {tape.proposals.length} fields mapped
                    {missing.length
                        ? ` · ${missing.length} required missing`
                        : ''}
                </Text>
                <Button
                    loading={committing}
                    disabled={missing.length > 0}
                    onClick={() =>
                        onCommit(
                            Object.fromEntries(
                                Object.entries(mapping)
                                    .filter(
                                        (e): e is [string, string] => !!e[1],
                                    )
                                    .map(([field, column]) => {
                                        const how = readAs[field];
                                        if (!how) return [field, column];
                                        return [
                                            field,
                                            how === 'percent'
                                                ? { column, unit: 'percent' as const }
                                                : { column, format: how },
                                        ];
                                    }),
                            ),
                        )
                    }
                >
                    {missing.length
                        ? `Map ${missing.map((m) => m.field).join(', ')}`
                        : 'Approve mapping and ingest'}
                </Button>
            </Group>
        </Stack>
    );
};

const TapeUploadModal: FC<Props> = ({ projectUuid, onClose }) => {
    const [dealId, setDealId] = useState('');
    const [role, setRole] = useState<TapeRole>('loans');
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState(0);
    const [staged, setStaged] = useState<TapeUpload | null>(null);
    const upload = useUploadTape(projectUuid, setProgress);
    const commit = useCommitTape(projectUuid);
    const { data: tapes } = useTapes(projectUuid, true);
    const dealTapes = (tapes ?? []).filter(
        (t) => t.dealId === dealId && t.status === 'committed',
    );

    return (
        <MantineModal
            opened
            onClose={onClose}
            title={staged ? 'Review column mapping' : 'Upload loan tape'}
            icon={IconUpload}
            size="xl"
        >
            <Stack>
                {!staged && (
                    <>
                        <Autocomplete
                            label="Deal"
                            placeholder="existing deal or a new deal id"
                            data={[
                                ...new Set((tapes ?? []).map((t) => t.dealId)),
                            ]}
                            value={dealId}
                            onChange={(v) => setDealId(v.toLowerCase())}
                        />
                        <SegmentedControl
                            value={role}
                            onChange={(v) => setRole(v as TapeRole)}
                            data={[
                                { value: 'loans', label: 'Loan tape' },
                                { value: 'payments', label: 'Payments tape' },
                            ]}
                        />
                        <FileInput
                            label="File (.csv or .csv.gz)"
                            accept=".csv,.gz"
                            value={file}
                            onChange={setFile}
                        />
                        {upload.isLoading && (
                            <Progress value={progress * 100} animated />
                        )}
                        {upload.error && (
                            <Text c="red" size="sm">
                                {upload.error.error?.message}
                            </Text>
                        )}
                        <Group justify="flex-end">
                            <Button
                                disabled={!file || !dealId}
                                loading={upload.isLoading}
                                onClick={() =>
                                    file &&
                                    upload.mutate(
                                        { file, dealId, role },
                                        { onSuccess: setStaged },
                                    )
                                }
                            >
                                Upload
                            </Button>
                        </Group>
                    </>
                )}
                {staged && staged.status === 'staged' && (
                    <MappingReview
                        tape={staged}
                        committing={commit.isLoading}
                        onCommit={(mapping) =>
                            commit.mutate(
                                { tapeUuid: staged.tapeUuid, mapping },
                                { onSuccess: setStaged },
                            )
                        }
                    />
                )}
                {commit.error && (
                    <Text c="red" size="sm">
                        {commit.error.error?.message}
                    </Text>
                )}
                {dealTapes.length > 0 && (
                    <Stack gap="xs" data-testid="tape-ingest-status">
                        <Text fw={500}>Deal {dealId}</Text>
                        {dealTapes.map((t) => (
                            <Group key={t.tapeUuid} gap="xs">
                                <Text size="sm">
                                    {t.role === 'loans'
                                        ? 'Loan tape'
                                        : 'Payments tape'}{' '}
                                    · {t.filename}
                                </Text>
                                <Badge
                                    color={
                                        STATUS_COLOR[t.ingestStatus ?? ''] ??
                                        'gray'
                                    }
                                >
                                    {t.ingestStatus}
                                </Badge>
                            </Group>
                        ))}
                        {dealTapes[0].ingestResult?.checks && (
                            <Table>
                                <Table.Tbody>
                                    {dealTapes[0].ingestResult.checks.map(
                                        (c) => (
                                            <Table.Tr key={c.checkId}>
                                                <Table.Td>
                                                    {c.severity}
                                                </Table.Td>
                                                <Table.Td>{c.title}</Table.Td>
                                                <Table.Td>{c.value}</Table.Td>
                                                <Table.Td>
                                                    {c.passed ? 'pass' : 'FAIL'}
                                                </Table.Td>
                                            </Table.Tr>
                                        ),
                                    )}
                                </Table.Tbody>
                            </Table>
                        )}
                        {staged && (
                            <Button
                                variant="default"
                                onClick={() => {
                                    setStaged(null);
                                    setFile(null);
                                    setProgress(0);
                                    setRole('payments');
                                }}
                            >
                                Upload another tape for this deal
                            </Button>
                        )}
                    </Stack>
                )}
            </Stack>
        </MantineModal>
    );
};

export default TapeUploadModal;
